import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, Qualification, Enrollment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useTheme } from '@flowcore-water/ui';
import { InstructorActivity } from './InstructorActivity';
import { DeadlineEditor } from './enrollment/DeadlineEditor';
import { enrollmentService } from '../services/enrollmentService';
import { SKILL_TREES } from '../data/skillTrees';
import { buildTimeline } from '../utils/timeline';
import { isItemCompleted } from '../utils/skillProgress';

interface DashboardProps {
  users: User[];
  qualifications: Qualification[];
}

type SortKey = 'name' | 'overall' | 'updated';

export const Dashboard: React.FC<DashboardProps> = ({ users, qualifications }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTheme();
  const { getProgress, preloadProgress } = useData();
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [deadlineEnrollment, setDeadlineEnrollment] = useState<{ enrollment: Enrollment; studentName: string } | null>(null);
  const [progressReady, setProgressReady] = useState(false);
  const refreshEnrollments = () => {
    enrollmentService.getAll().then(setEnrollments).catch(console.error);
  };

  useEffect(() => {
    refreshEnrollments();
  }, []);

  // Preload progress for all enrolled users so QualSummary works immediately
  useEffect(() => {
    if (enrollments.length === 0) return;
    const enrolledUserIds = [...new Set(enrollments.map((e) => e.userId))];
    preloadProgress(enrolledUserIds).then(() => setProgressReady(true));
  }, [enrollments, preloadProgress]);

  const isStaff = user?.role === 'admin' || user?.role === 'instructor' || user?.role === 'outside_observer';
  const canManage = user?.role === 'admin' || user?.role === 'instructor';

  // Show non-deactivated users who participate in training (exclude observer admins)
  const filteredUsers = useMemo(() => {
    return users
      .filter(
        (u) =>
          (u.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(searchQuery.toLowerCase())) &&
          !u.deactivated &&
          !u.excludeFromProgress &&
          u.role !== 'outside_observer'
      );
  }, [users, searchQuery]);

  const getUserEnrollments = (userId: string): Enrollment[] => {
    return enrollments.filter((e) => e.userId === userId);
  };

  const getEnrollmentProgress = (userId: string, enrollment: Enrollment): number => {
    const tree = SKILL_TREES.find((tr) => tr.id === enrollment.skillTreeId);
    if (!tree) return 0;
    const treeQuals = qualifications.filter((q) => tree.qualificationIds.includes(q.id));
    const totalItems = treeQuals.reduce((sum, q) => sum + q.items.length, 0);
    if (totalItems === 0) return 0;
    const progress = getProgress(userId);
    let completed = 0;
    for (const qual of treeQuals) {
      for (const item of qual.items) {
        if (isItemCompleted(progress, qual.id, item.id)) completed++;
      }
    }
    return Math.round((completed / totalItems) * 100);
  };

  const getWorstPaceStatus = (userId: string): string => {
    const userEnrs = getUserEnrollments(userId);
    if (userEnrs.length === 0) return 'not-enrolled';
    const progress = getProgress(userId);
    const statusPriority: Record<string, number> = { complete: 0, 'no-deadline': 1, 'on-track': 2, 'at-risk': 3, behind: 4, overdue: 5 };
    let worst = 'on-track';
    for (const enr of userEnrs) {
      const tree = SKILL_TREES.find((tr) => tr.id === enr.skillTreeId);
      if (!tree) continue;
      const timeline = buildTimeline(enr, qualifications, tree.qualificationIds, progress || {});
      for (const entry of timeline) {
        if ((statusPriority[entry.status] || 0) > (statusPriority[worst] || 0)) {
          worst = entry.status;
        }
      }
    }
    return worst;
  };

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];
    switch (sortKey) {
      case 'name':
        sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
        break;
      case 'overall':
        sorted.sort((a, b) => {
          const aEnrs = getUserEnrollments(a.uid);
          const bEnrs = getUserEnrollments(b.uid);
          const aAvg = aEnrs.length > 0 ? aEnrs.reduce((s, e) => s + getEnrollmentProgress(a.uid, e), 0) / aEnrs.length : 0;
          const bAvg = bEnrs.length > 0 ? bEnrs.reduce((s, e) => s + getEnrollmentProgress(b.uid, e), 0) / bEnrs.length : 0;
          return bAvg - aAvg;
        });
        break;
      case 'updated':
        sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }
    return sorted;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers, sortKey, enrollments, progressReady]);

  const handleToggleExpand = (userId: string) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  const getPaceColor = (status: string): string => ({
    complete: t.success,
    'on-track': t.success,
    'at-risk': t.warn,
    behind: t.fail,
    overdue: t.fail,
    'no-deadline': t.textMuted,
    'not-enrolled': t.textMuted,
  } as Record<string, string>)[status] ?? t.textMuted;

  const paceLabel: Record<string, string> = {
    complete: 'Done',
    'on-track': 'On Track',
    'at-risk': 'At Risk',
    behind: 'Behind',
    overdue: 'Overdue',
    'no-deadline': '—',
    'not-enrolled': 'Not Enrolled',
  };

  const enrolledCount = useMemo(() => {
    const enrolledUserIds = new Set(enrollments.map((e) => e.userId));
    return enrolledUserIds.size;
  }, [enrollments]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>
          {isStaff ? 'Progress Dashboard' : 'Activity'}
        </h1>
        <p className="mt-2" style={{ color: t.textSecondary }}>
          {isStaff ? 'Track student progress and manage enrollments' : 'Recent signoff activity'}
        </p>
      </div>

      {isStaff && (
        <>
          {/* Search & Sort */}
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-1"
                style={{
                  background: t.inputBg,
                  borderColor: t.border,
                  color: t.textPrimary,
                }}
              />
            </div>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-1"
              style={{
                background: t.inputBg,
                borderColor: t.border,
                color: t.textPrimary,
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="overall">Sort by Progress</option>
              <option value="updated">Sort by Last Updated</option>
            </select>
          </div>

          {/* Users Table */}
          <div className="rounded-lg border overflow-hidden" style={{ background: t.cardBg, borderColor: t.border }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ background: t.tableHeaderBg, borderColor: t.border }}>
                    <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Name</th>
                    <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Qualifications</th>
                    <th className="px-6 py-3 text-left font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Pace</th>
                    <th className="px-6 py-3 text-right font-bold text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => {
                    const userEnrs = getUserEnrollments(u.uid);
                    const pace = getWorstPaceStatus(u.uid);
                    const isExpanded = expandedUserId === u.uid;

                    return (
                      <React.Fragment key={u.uid}>
                        <tr
                          onClick={() => handleToggleExpand(u.uid)}
                          className="border-b hover:bg-white/5 cursor-pointer transition-colors"
                          style={{ borderColor: t.border }}
                        >
                          <td className="px-6 py-4">
                            <div>
                              <span className="font-medium" style={{ color: t.textPrimary }}>{u.displayName}</span>
                              <span className="ml-2 text-xs font-mono" style={{ color: t.textSecondary }}>{u.email}</span>
                              {u.role !== 'individual' && (
                                <span className="ml-2 text-xs uppercase px-1.5 py-0.5 rounded" style={{ background: t.accent + '20', color: t.accent }}>{u.role}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <QualSummary userId={u.uid} enrollments={userEnrs} qualifications={qualifications} getProgress={getProgress} />
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className="rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
                              style={{
                                background: getPaceColor(pace) + '20',
                                color: getPaceColor(pace),
                                border: `1px solid ${getPaceColor(pace)}40`,
                              }}
                            >
                              {paceLabel[pace]}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/tree/${u.uid}`); }}
                              className="text-xs font-medium px-3 py-1 rounded border transition-colors"
                              style={{ borderColor: t.border, color: t.textSecondary }}
                            >
                              View Tree
                            </button>
                          </td>
                        </tr>

                        {/* Expanded enrollment details */}
                        {isExpanded && (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-6 py-4 border-b"
                              style={{
                                background: t.expandedRowBg,
                                borderColor: t.border,
                                boxShadow: `inset 0 2px 8px rgba(0,0,0,0.25), inset 0 -1px 0 ${t.border}`,
                              }}
                            >
                              <div className="space-y-3">
                                {userEnrs.length > 0 ? (
                                  userEnrs.map((enr) => (
                                    <EnrollmentRow
                                      key={enr.id}
                                      enrollment={enr}
                                      userId={u.uid}
                                      qualifications={qualifications}
                                      getProgress={getProgress}
                                      getEnrollmentProgress={getEnrollmentProgress}
                                      onEditDeadlines={canManage ? () => setDeadlineEnrollment({ enrollment: enr, studentName: u.displayName }) : undefined}
                                      onRemove={canManage ? async () => {
                                        await enrollmentService.remove(enr.id, user.uid);
                                        refreshEnrollments();
                                      } : undefined}
                                    />
                                  ))
                                ) : (
                                  <p className="text-xs" style={{ color: t.textSecondary }}>No enrollments yet.</p>
                                )}
                                {canManage && (
                                  <InlineEnrollForm
                                    userId={u.uid}
                                    existingTreeIds={userEnrs.map((e) => e.skillTreeId)}
                                    createdBy={user?.uid || ''}
                                    onEnroll={refreshEnrollments}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {sortedUsers.length === 0 && (
              <div className="px-6 py-12 text-center">
                <p style={{ color: t.textSecondary }}>No users found</p>
              </div>
            )}
          </div>

          {/* Summary Stats */}
          <div className="rounded-lg border p-6" style={{ background: t.cardBg, borderColor: t.border }}>
            <h2 className="font-bold mb-4 text-sm uppercase tracking-wide" style={{ color: t.textPrimary }}>Summary</h2>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs mb-1 uppercase tracking-wide" style={{ color: t.textSecondary }}>Users</p>
                <p className="text-2xl font-bold" style={{ color: t.accent }}>{filteredUsers.length}</p>
              </div>
              <div>
                <p className="text-xs mb-1 uppercase tracking-wide" style={{ color: t.textSecondary }}>Enrolled</p>
                <p className="text-2xl font-bold" style={{ color: t.accent }}>{enrolledCount}</p>
              </div>
              <div>
                <p className="text-xs mb-1 uppercase tracking-wide" style={{ color: t.textSecondary }}>Skill Trees</p>
                <p className="text-2xl font-bold" style={{ color: t.accent }}>{SKILL_TREES.length}</p>
              </div>
              <div>
                <p className="text-xs mb-1 uppercase tracking-wide" style={{ color: t.textSecondary }}>Enrollments</p>
                <p className="text-2xl font-bold" style={{ color: t.accent }}>{enrollments.length}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Activity Feed */}
      <InstructorActivity users={users} />

      {/* Deadline Editor Modal */}
      {deadlineEnrollment && (
        <DeadlineEditor
          enrollment={deadlineEnrollment.enrollment}
          studentName={deadlineEnrollment.studentName}
          onClose={() => setDeadlineEnrollment(null)}
          onSave={async () => { refreshEnrollments(); setDeadlineEnrollment(null); }}
        />
      )}
    </div>
  );
};

/** Qualification summary: completed quals as green chips, in-progress as mini bars */
const QualSummary: React.FC<{
  userId: string;
  enrollments: Enrollment[];
  qualifications: Qualification[];
  getProgress: (uid: string) => Record<string, any>;
}> = ({ userId, enrollments, qualifications, getProgress }) => {
  const { t } = useTheme();
  if (enrollments.length === 0) {
    return <span className="text-xs  italic" style={{ color: t.textMuted }}>No enrollments</span>;
  }

  const progress = getProgress(userId);
  const completed: string[] = [];
  const inProgress: { shortName: string; pct: number }[] = [];

  for (const enr of enrollments) {
    const tree = SKILL_TREES.find((tr) => tr.id === enr.skillTreeId);
    if (!tree) continue;
    for (const qualId of tree.qualificationIds) {
      const qual = qualifications.find((q) => q.id === qualId);
      if (!qual) continue;
      const total = qual.items.length;
      if (total === 0) continue;
      let done = 0;
      for (const item of qual.items) {
        if (isItemCompleted(progress, qualId, item.id)) done++;
      }
      if (done >= total) {
        completed.push(qual.shortName);
      } else if (done > 0) {
        inProgress.push({ shortName: qual.shortName, pct: Math.round((done / total) * 100) });
      }
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Completed quals as stacked green chips */}
      {completed.map((name) => (
        <span
          key={name}
          className="text-xs font-semibold px-2 py-0.5 rounded"
          style={{ background: t.successBg, color: t.success, border: `1px solid ${t.successBorder}` }}
        >
          {name} ✓
        </span>
      ))}
      {/* In-progress quals as mini bars */}
      {inProgress.map((ip) => (
        <div key={ip.shortName} className="flex items-center gap-1.5">
          <span className="text-xs font-medium " style={{ color: t.textMuted }}>{ip.shortName}</span>
          <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: t.progressTrack }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${ip.pct}%`, background: ip.pct >= 50 ? t.warn : t.info }}
            />
          </div>
          <span className="text-xs " style={{ color: t.textMuted }}>{ip.pct}%</span>
        </div>
      ))}
      {completed.length === 0 && inProgress.length === 0 && (
        <span className="text-xs " style={{ color: t.textMuted }}>Not started</span>
      )}
    </div>
  );
};

/** Single enrollment row in the expanded section */
const EnrollmentRow: React.FC<{
  enrollment: Enrollment;
  userId: string;
  qualifications: Qualification[];
  getProgress: (uid: string) => Record<string, any>;
  getEnrollmentProgress: (uid: string, enr: Enrollment) => number;
  onEditDeadlines?: () => void;
  onRemove?: () => void;
}> = ({ enrollment, userId, qualifications, getProgress, getEnrollmentProgress, onEditDeadlines, onRemove }) => {
  const { t } = useTheme();
  const tree = SKILL_TREES.find((tr) => tr.id === enrollment.skillTreeId);
  const pct = getEnrollmentProgress(userId, enrollment);
  const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Build timeline for status
  const progress = getProgress(userId);
  const timeline = tree ? buildTimeline(enrollment, qualifications, tree.qualificationIds, progress || {}) : [];
  const statusPriority: Record<string, number> = { complete: 0, 'no-deadline': 1, 'on-track': 2, 'at-risk': 3, behind: 4, overdue: 5 };
  let worst = 'on-track';
  for (const entry of timeline) {
    if ((statusPriority[entry.status] || 0) > (statusPriority[worst] || 0)) worst = entry.status;
  }

  const getStatusColor = (status: string): string => ({
    complete: t.success,
    'on-track': t.success,
    'at-risk': t.warn,
    behind: t.fail,
    overdue: t.fail,
    'no-deadline': t.textMuted,
  } as Record<string, string>)[status] ?? t.textMuted;

  return (
    <div className="flex items-center justify-between rounded-lg border px-4 py-3" style={{ background: 'rgba(0,0,0,0.2)', borderColor: 'rgba(71,85,105,0.4)' }}>
      <div className="flex items-center gap-4">
        <div>
          <span className="text-sm font-medium " style={{ color: t.textSecondary }}>{tree?.name || enrollment.skillTreeId}</span>
          <span className="ml-2 text-xs " style={{ color: t.textMuted }}>Started {fmtDate(enrollment.startDate)}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: t.progressTrack }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: pct === 100 ? t.progressFill : pct >= 50 ? t.warn : t.info }}
            />
          </div>
          <span className="text-xs font-bold " style={{ color: t.textSecondary }}>{pct}%</span>
        </div>
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ background: getStatusColor(worst) + '20', color: getStatusColor(worst), border: `1px solid ${getStatusColor(worst)}40` }}
        >
          {worst === 'on-track' ? 'On Track' : worst === 'at-risk' ? 'At Risk' : worst.charAt(0).toUpperCase() + worst.slice(1)}
        </span>
      </div>
      {(onEditDeadlines || onRemove) && (
        <div className="flex items-center gap-2">
          {onEditDeadlines && (
            <button
              onClick={onEditDeadlines}
              className="text-xs px-2.5 py-1 rounded border hover:transition-colors" style={{ color: t.textSecondary }}
            >
              Deadlines
            </button>
          )}
          {onRemove && (
            <button
              onClick={onRemove}
              className="text-xs px-2.5 py-1 rounded border border-red-900/40  hover:text-red-300 transition-colors" style={{ color: t.fail }}
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** Inline enrollment creation form */
const InlineEnrollForm: React.FC<{
  userId: string;
  existingTreeIds: string[];
  createdBy: string;
  onEnroll: () => void;
}> = ({ userId, existingTreeIds, createdBy, onEnroll }) => {
  const { t } = useTheme();
  const [selectedTree, setSelectedTree] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [enrolling, setEnrolling] = useState(false);

  const availableTrees = SKILL_TREES.filter((tr) => !existingTreeIds.includes(tr.id));

  const handleEnroll = async () => {
    if (!selectedTree) return;
    setEnrolling(true);
    try {
      await enrollmentService.create(
        userId,
        selectedTree,
        new Date(startDate + 'T00:00:00'),
        createdBy,
      );
      setSelectedTree('');
      onEnroll();
    } catch (err) {
      console.error('Enrollment failed:', err);
    } finally {
      setEnrolling(false);
    }
  };

  if (availableTrees.length === 0) return null;

  return (
    <div className="flex items-center gap-3 pt-2 border-t /40">
      <select
        value={selectedTree}
        onChange={(e) => setSelectedTree(e.target.value)}
        className="rounded border px-3 py-1.5 text-xs" style={{ color: t.textSecondary, background: t.pageBg }}
      >
        <option value="">+ Enroll in tree...</option>
        {availableTrees.map((tr) => (
          <option key={tr.id} value={tr.id}>{tr.name}</option>
        ))}
      </select>
      {selectedTree && (
        <>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded border px-3 py-1.5 text-xs" style={{ color: t.textSecondary, background: t.pageBg }}
          />
          <button
            onClick={handleEnroll}
            disabled={enrolling}
            className="rounded px-3 py-1.5 text-xs font-semibold transition-colors" style={{ color: t.textPrimary, background: enrolling ? t.surfaceHover : t.accentDim }}
          >
            {enrolling ? 'Enrolling...' : 'Enroll'}
          </button>
        </>
      )}
    </div>
  );
};
